import React from 'react';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { AccentedText } from '../AccentedText/AccentedText';
import { ThemedIcon, IFormIcons } from '../ThemedIcon/ThemedIcon';
import { filterArrayOfObjByKey } from '../../shared/Utils';
import { stripHTMLStrings } from '../../shared/stripHtml';
import { DynamicForm } from '../DynamicForm/DynamicForm';
import { IDynamicForm } from '../DynamicForm/IDynamicFormTypes';
import '../TwoColumnFormText/TwoColumnFormText.scss';
import '../QuoteText/QuoteText.scss';

export interface ITwoColumnDynamicForm {
    theme: `white` | `light` | `gray` | `dark`;
    contextCol: ITwoColumnFormTextContext;
    formCol: IDynamicForm;
}

export interface ITwoColumnFormTextContext {
    eyebrow?: string;
    heading?: string;
    headingOption: boolean;
    contextCopy?: string;
    contextMediaData?: IContextMedia;
    formIcons?: IFormIcons[];
    formQuotes?: IFormQuotes[];
    conclusion: string;
}

interface IContextMedia {
    contextMedia?: IGatsbyImageData;
    contextMediaMobile: boolean;
    contextMediaAlt?: string;
}

interface IFormQuotes {
    speaker?: string;
    quoteText?: string;
}

export const TwoColumnDynamicForm = (props: ITwoColumnDynamicForm) => {
    if (!props.formCol) return null;
    const {
        eyebrow,
        heading,
        headingOption,
        contextCopy,
        contextMediaData,
        formIcons,
        formQuotes,
        conclusion,
    } = props.contextCol;
    const { submissionUrl, id, disclaimer, formHeadingData, successPageUrl } =
        props.formCol;
    const filteredQuotes: IFormQuotes[] = filterArrayOfObjByKey(
        formQuotes,
        `quoteText`
    );
    const filteredIcons: IFormIcons[] = filterArrayOfObjByKey(
        formIcons,
        `iconText`
    );
    const HeadingEl = ({ children }) => {
        const headingChildren = children.filter((child) => child);
        if (headingChildren.length <= 0) return null;
        if (headingOption) {
            return <h1>{headingChildren}</h1>;
        } else {
            return <h2>{headingChildren}</h2>;
        }
    };

    return (
        <section
            className={`themed-container c--two-column-dynamic-form ${props.theme?.toLowerCase()}`}
        >
            <div className="kite-container">
                <div id={`formContextColumn`} className={`form-context-column`}>
                    {(heading || eyebrow || contextCopy) && (
                        <div className="c--text-content">
                            <HeadingEl>
                                {eyebrow && (
                                    <span className="eyebrow">{eyebrow}</span>
                                )}
                                {heading && (
                                    <span>
                                        <AccentedText>{heading}</AccentedText>
                                    </span>
                                )}
                            </HeadingEl>
                            {contextCopy && (
                                <div
                                    className="body"
                                    dangerouslySetInnerHTML={{
                                        __html: contextCopy,
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {contextMediaData?.contextMedia && (
                        <GatsbyImage
                            image={getImage(contextMediaData.contextMedia)}
                            alt={contextMediaData.contextMediaAlt ?? ``}
                            className={`${
                                contextMediaData?.contextMediaMobile
                                    ? ``
                                    : `mobile-disabled`
                            }`}
                        />
                    )}

                    {filteredIcons?.length > 0 && (
                        <ul className={`form-icons`}>
                            {filteredIcons.map(
                                ({ iconAltTag, iconText, iconImg }, idx) => (
                                    <ThemedIcon
                                        key={idx}
                                        iconAltTag={iconAltTag}
                                        iconText={iconText}
                                        iconImg={iconImg}
                                        background={props.theme}
                                    />
                                )
                            )}
                        </ul>
                    )}
                    {filteredQuotes?.length > 0 && (
                        <div className={`quote-content`}>
                            {filteredQuotes.map((quote, idx) => (
                                <figure key={idx}>
                                    {quote?.quoteText && (
                                        <blockquote
                                            className="typestack-title-1"
                                            dangerouslySetInnerHTML={{
                                                __html: quote?.quoteText,
                                            }}
                                        />
                                    )}
                                    {quote?.speaker && (
                                        <figcaption className="author typestack-body-1">
                                            - {quote?.speaker ?? `Anonymous`}
                                        </figcaption>
                                    )}
                                </figure>
                            ))}
                        </div>
                    )}
                    {conclusion && (
                        <div className={`conclusion`}>
                            <p
                                className="typestack-body-1"
                                dangerouslySetInnerHTML={{
                                    __html: stripHTMLStrings(conclusion),
                                }}
                            ></p>
                        </div>
                    )}
                </div>
                <DynamicForm
                    node={props.formCol.node}
                    submissionUrl={submissionUrl}
                    dynamicFormID={id}
                    disclaimer={disclaimer}
                    formHeadingData={formHeadingData}
                    successPageUrl={successPageUrl}
                />
            </div>
        </section>
    );
};
